import { useMemo, useRef, useState } from 'react';
import { initials } from '../../components/Customers.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t, tn, type MessageKey } from '../../i18n/index.ts';
import { action, upload } from '../../lib/api.ts';
import { day as dayText, todayIso } from '../../lib/format.ts';
import { useSession } from '../../lib/session.tsx';
import { TONES, useAction, useSave } from '../manage/common.tsx';
import { useLinkParam } from '../../lib/link-params.ts';

/**
 * The staff directory: everyone's card (job, department, who they report to, how to reach them,
 * whether they are off), the organisation chart built from the "reports to" links, and the
 * departments. Managers set the work side; each person fills in their own private side.
 */

interface Person {
  id: string;
  name: string;
  roles: string[];
  job_title: string | null;
  department: string | null;
  manager: string | null;
  location: string | null;
  work_phone: string | null;
  work_email: string | null;
  start_date: string | null;
  photo: string | null;
  /** Day and month ("03-14") when the person shares it. */
  birthday: string | null;
  off_until: string | null;
}
interface Department {
  id: string;
  name: string;
  parent: string | null;
  manager: string | null;
  color: number;
  note: string | null;
  people: number;
  open_jobs: number;
}
interface Directory {
  today: string;
  people: Person[];
  departments: Department[];
  can: { manage: boolean };
}
interface Mini {
  id: string;
  name: string;
  job_title: string | null;
  photo: string | null;
}
interface Card {
  id: string;
  name: string;
  active: boolean;
  roles: string[];
  job_title: string | null;
  department: { id: string; name: string } | null;
  department_path: string[];
  location: { id: string; name: string } | null;
  work_phone: string | null;
  work_email: string | null;
  start_date: string | null;
  about: string | null;
  photo: string | null;
  birthday: string | null;
  share_birthday: boolean;
  private: { emergency_name: string | null; emergency_phone: string | null; private_phone: string | null } | null;
  manager: Mini | null;
  chain: Mini[];
  reports: Mini[];
  off_until: string | null;
  next_leave: { start: string; end: string } | null;
  can: { edit_work: boolean; edit_own: boolean; photo: boolean };
}
interface Choices {
  people: { id: string; name: string }[];
  locations: { id: string; name: string }[];
  departments: { id: string; name: string; parent: string | null }[];
}
type View = 'people' | 'chart' | 'departments';

const toneOf = (n: number) => TONES[((n % TONES.length) + TONES.length) % TONES.length]!;
const hash = (s: string) => [...s].reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 7);

export function Avatar({ name, photo, size = 'md' }: { name: string; photo: string | null; size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  return photo ? (
    <img className={`dir-avatar ${size}`} src={photo} alt="" />
  ) : (
    <span className={`dir-avatar ${size} tone-${toneOf(hash(name))}`} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

/** "3 years", "5 months", "New": how long someone has worked here. */
function tenure(start: string | null, today: string) {
  if (!start || start > today) return null;
  const months = (Number(today.slice(0, 4)) - Number(start.slice(0, 4))) * 12 + Number(today.slice(5, 7)) - Number(start.slice(5, 7)) - (today.slice(8) < start.slice(8) ? 1 : 0);
  if (months < 1) return t('dir.new_here');
  if (months < 12) return tn('dir.months', months);
  return tn('dir.years', Math.floor(months / 12));
}

/** Days until the next birthday (0 = today), from a "MM-DD". */
function daysToBirthday(md: string, today: string) {
  const year = Number(today.slice(0, 4));
  const at = (y: number) => Date.UTC(y, Number(md.slice(0, 2)) - 1, Number(md.slice(3, 5)));
  const now = Date.UTC(year, Number(today.slice(5, 7)) - 1, Number(today.slice(8, 10)));
  const next = at(year) >= now ? at(year) : at(year + 1);
  return Math.round((next - now) / 86_400_000);
}
const birthdayText = (md: string) => new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(`2000-${md}T12:00:00Z`));

/** A department and all the departments inside it. */
function withInside(id: string, list: { id: string; parent: string | null }[]) {
  const out = new Set([id]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const d of list)
      if (d.parent && out.has(d.parent) && !out.has(d.id)) {
        out.add(d.id);
        grew = true;
      }
  }
  return out;
}

export function DirectoryTab() {
  const { data, reload } = useAction<Directory>('hr.directory');
  const [view, setView] = useState<View>('people');
  const [q, setQ] = useState('');
  const [dep, setDep] = useState<string>('');
  const [open, setOpen] = useState<string | null>(null);
  // The search opens one person's card: /people?tab=team&open=<user id>.
  useLinkParam('open', setOpen);
  const [depForm, setDepForm] = useState<Department | 'new' | null>(null);

  if (!data) return <Skeleton h={320} r="var(--radius-lg)" />;
  const deps = new Map(data.departments.map((d) => [d.id, d]));
  const views: { key: View; icon: 'users' | 'layers' | 'folder'; label: string }[] = [
    { key: 'people', icon: 'users', label: t('dir.v_people') },
    { key: 'chart', icon: 'layers', label: t('dir.v_chart') },
    { key: 'departments', icon: 'folder', label: t('dir.v_departments') },
  ];
  return (
    <section className="panel dir-panel">
      <div className="panel-head dir-head">
        <div className="segmented" role="tablist" aria-label={t('dir.views')}>
          {views.map((v) => (
            <button key={v.key} role="tab" aria-selected={view === v.key} className={view === v.key ? 'active' : ''} onClick={() => setView(v.key)}>
              <Icon name={v.icon} size={16} />
              {v.label}
            </button>
          ))}
        </div>
        {view === 'departments' && data.can.manage ? (
          <Button variant="primary" icon="plus" onClick={() => setDepForm('new')}>
            {t('dir.new_department')}
          </Button>
        ) : null}
      </div>
      {view === 'people' ? (
        <PeopleView data={data} deps={deps} q={q} setQ={setQ} dep={dep} setDep={setDep} onOpen={setOpen} />
      ) : view === 'chart' ? (
        <ChartView data={data} deps={deps} onOpen={setOpen} />
      ) : (
        <DepartmentsView
          data={data}
          onOpen={setOpen}
          onShow={(id) => {
            setDep(id);
            setQ('');
            setView('people');
          }}
          onEdit={(d) => setDepForm(d)}
          onChange={reload}
        />
      )}
      {open ? (
        <PersonDialog
          key={open}
          id={open}
          today={data.today}
          onOpen={setOpen}
          onClose={() => setOpen(null)}
          onChange={async () => {
            await reload();
          }}
        />
      ) : null}
      {depForm ? (
        <DepartmentForm
          initial={depForm === 'new' ? null : depForm}
          all={data.departments}
          onClose={() => setDepForm(null)}
          onSaved={async () => {
            setDepForm(null);
            await reload();
          }}
        />
      ) : null}
    </section>
  );
}

// ---- people ------------------------------------------------------------------------------

function PeopleView({
  data,
  deps,
  q,
  setQ,
  dep,
  setDep,
  onOpen,
}: {
  data: Directory;
  deps: Map<string, Department>;
  q: string;
  setQ: (v: string) => void;
  dep: string;
  setDep: (v: string) => void;
  onOpen: (id: string) => void;
}) {
  const names = new Map(data.people.map((p) => [p.id, p.name]));
  const inDep = dep && dep !== 'none' ? withInside(dep, data.departments) : null;
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const list = data.people.filter((p) => {
    if (dep === 'none' ? p.department : inDep && !(p.department && inDep.has(p.department))) return false;
    const hay = [p.name, p.job_title, p.department ? deps.get(p.department)?.name : null, p.location, p.work_phone, p.work_email, ...p.roles.map((r) => t(`role.${r}` as MessageKey))]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  const soon = data.people
    .filter((p) => p.birthday)
    .map((p) => ({ p, in: daysToBirthday(p.birthday!, data.today) }))
    .filter((x) => x.in <= 14)
    .sort((a, b) => a.in - b.in);
  const fresh = data.people.filter((p) => p.start_date && p.start_date <= data.today && p.start_date >= addDaysIso(data.today, -30));
  const off = data.people.filter((p) => p.off_until);
  const unplaced = data.people.filter((p) => !p.department).length;
  return (
    <>
      {soon.length || fresh.length || off.length ? (
        <div className="dir-highlights">
          {off.length ? (
            <div className="dir-highlight">
              <span className="dir-hl-icon tone-orange">
                <Icon name="sun" size={18} />
              </span>
              <div>
                <strong>{tn('dir.off_today', off.length)}</strong>
                <span className="dir-hl-people">
                  {off.map((p) => (
                    <button key={p.id} type="button" className="link-button" onClick={() => onOpen(p.id)}>
                      {p.name}
                    </button>
                  ))}
                </span>
              </div>
            </div>
          ) : null}
          {soon.length ? (
            <div className="dir-highlight">
              <span className="dir-hl-icon tone-purple">
                <Icon name="gift" size={18} />
              </span>
              <div>
                <strong>{t('dir.birthdays_soon')}</strong>
                <span className="dir-hl-people">
                  {soon.map(({ p, in: n }) => (
                    <button key={p.id} type="button" className="link-button" onClick={() => onOpen(p.id)}>
                      {p.name} · {n === 0 ? t('dir.today') : n === 1 ? t('dir.tomorrow') : birthdayText(p.birthday!)}
                    </button>
                  ))}
                </span>
              </div>
            </div>
          ) : null}
          {fresh.length ? (
            <div className="dir-highlight">
              <span className="dir-hl-icon tone-green">
                <Icon name="sparkle" size={18} />
              </span>
              <div>
                <strong>{t('dir.new_people')}</strong>
                <span className="dir-hl-people">
                  {fresh.map((p) => (
                    <button key={p.id} type="button" className="link-button" onClick={() => onOpen(p.id)}>
                      {p.name}
                    </button>
                  ))}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="dir-tools">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('dir.search')} aria-label={t('dir.search')} onChange={(e) => setQ(e.target.value)} />
        </label>
        <select className="input dir-dep-filter" value={dep} aria-label={t('dir.department')} onChange={(e) => setDep(e.target.value)}>
          <option value="">{t('dir.all_departments')}</option>
          {departmentOptions(data.departments).map((d) => (
            <option key={d.id} value={d.id}>
              {' '.repeat(d.depth)}
              {d.name}
            </option>
          ))}
          {unplaced ? <option value="none">{t('dir.no_department_n', { n: unplaced })}</option> : null}
        </select>
        <span className="muted small dir-count">{tn('dir.people_n', list.length)}</span>
      </div>
      {list.length ? (
        <ul className="dir-grid">
          {list.map((p) => {
            const d = p.department ? deps.get(p.department) : null;
            const bday = p.birthday ? daysToBirthday(p.birthday, data.today) : null;
            return (
              <li key={p.id}>
                <button type="button" className="dir-card" onClick={() => onOpen(p.id)}>
                  <span className="dir-card-top">
                    <Avatar name={p.name} photo={p.photo} size="lg" />
                    <span className="dir-card-who">
                      <strong>{p.name}</strong>
                      <span className="muted">{p.job_title ?? (p.roles[0] ? t(`role.${p.roles[0]}` as MessageKey) : t('dir.no_title'))}</span>
                    </span>
                  </span>
                  <span className="dir-card-tags">
                    {d ? <span className={`dir-dep tone-${toneOf(d.color)}`}>{d.name}</span> : null}
                    {p.off_until ? <StatusBadge tone="warn" icon="sun" label={t('dir.off_until', { day: dayText(p.off_until) })} /> : null}
                    {bday !== null && bday <= 7 ? (
                      <StatusBadge tone="primary" icon="gift" label={bday === 0 ? t('dir.birthday_today') : t('dir.birthday_on', { day: birthdayText(p.birthday!) })} />
                    ) : null}
                  </span>
                  <span className="dir-card-facts">
                    {p.manager && names.get(p.manager) ? (
                      <span>
                        <Icon name="up" size={14} />
                        {t('dir.reports_to_x', { name: names.get(p.manager)! })}
                      </span>
                    ) : null}
                    {p.location ? (
                      <span>
                        <Icon name="pin" size={14} />
                        {p.location}
                      </span>
                    ) : null}
                    {p.work_phone ? (
                      <span>
                        <Icon name="phone" size={14} />
                        {p.work_phone}
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState icon="search" title={t('dir.no_match')} body={t('dir.no_match_hint')} />
      )}
    </>
  );
}

const addDaysIso = (d: string, n: number) => new Date(Date.parse(`${d}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** Departments in tree order with their depth, for a select. */
function departmentOptions(list: { id: string; name: string; parent: string | null }[], skip?: Set<string>) {
  const out: { id: string; name: string; depth: number }[] = [];
  const known = new Set(list.map((d) => d.id));
  const walk = (parent: string | null, depth: number) => {
    for (const d of list.filter((x) => (parent ? x.parent === parent : !x.parent || !known.has(x.parent))).sort((a, b) => a.name.localeCompare(b.name))) {
      if (skip?.has(d.id)) continue;
      out.push({ id: d.id, name: d.name, depth });
      if (depth < 8) walk(d.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

// ---- organisation chart ------------------------------------------------------------------

function ChartView({ data, deps, onOpen }: { data: Directory; deps: Map<string, Department>; onOpen: (id: string) => void }) {
  const ids = new Set(data.people.map((p) => p.id));
  const children = useMemo(() => {
    const m = new Map<string | null, Person[]>();
    for (const p of data.people) {
      const up = p.manager && ids.has(p.manager) && p.manager !== p.id ? p.manager : null;
      m.set(up, [...(m.get(up) ?? []), p]);
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const roots = (children.get(null) ?? []).slice().sort((a, b) => (children.get(b.id)?.length ?? 0) - (children.get(a.id)?.length ?? 0) || a.name.localeCompare(b.name));
  const leaders = roots.filter((r) => children.get(r.id)?.length);
  const loose = roots.filter((r) => !children.get(r.id)?.length);
  if (!leaders.length) return <EmptyState icon="layers" title={t('dir.chart_empty')} body={data.can.manage ? t('dir.chart_empty_hint_manage') : t('dir.chart_empty_hint')} />;
  return (
    <div className="dir-chart">
      <ul className="org-tree" role="tree" aria-label={t('dir.v_chart')}>
        {leaders.map((p) => (
          <OrgNode key={p.id} p={p} depth={0} kids={children} deps={deps} onOpen={onOpen} />
        ))}
      </ul>
      {loose.length ? (
        <div className="dir-loose">
          <h3>{t('dir.no_manager')}</h3>
          <p className="muted small">{t('dir.no_manager_hint')}</p>
          <ul className="dir-chips">
            {loose.map((p) => (
              <li key={p.id}>
                <button type="button" className="dir-chip" onClick={() => onOpen(p.id)}>
                  <Avatar name={p.name} photo={p.photo} size="sm" />
                  <span>{p.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function OrgNode({ p, depth, kids, deps, onOpen }: { p: Person; depth: number; kids: Map<string | null, Person[]>; deps: Map<string, Department>; onOpen: (id: string) => void }) {
  const list = (kids.get(p.id) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
  const [open, setOpen] = useState(depth < 2);
  const d = p.department ? deps.get(p.department) : null;
  return (
    <li role="treeitem" aria-expanded={list.length ? open : undefined} aria-selected={false}>
      <div className="org-node">
        <button type="button" className="org-card" onClick={() => onOpen(p.id)}>
          <Avatar name={p.name} photo={p.photo} />
          <span className="org-who">
            <strong>{p.name}</strong>
            <span className="muted small">{p.job_title ?? t('dir.no_title')}</span>
            {d ? <span className={`dir-dep tone-${toneOf(d.color)}`}>{d.name}</span> : null}
          </span>
        </button>
        {list.length ? (
          <button type="button" className="org-toggle" aria-label={open ? t('dir.hide_team', { name: p.name }) : t('dir.show_team', { name: p.name })} onClick={() => setOpen(!open)}>
            <Icon name={open ? 'up' : 'down'} size={14} />
            {list.length}
          </button>
        ) : null}
      </div>
      {list.length && open ? (
        <ul role="group">
          {list.map((c) => (
            <OrgNode key={c.id} p={c} depth={depth + 1} kids={kids} deps={deps} onOpen={onOpen} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

// ---- departments -------------------------------------------------------------------------

function DepartmentsView({
  data,
  onOpen,
  onShow,
  onEdit,
  onChange,
}: {
  data: Directory;
  onOpen: (id: string) => void;
  onShow: (id: string) => void;
  onEdit: (d: Department) => void;
  onChange: () => Promise<void>;
}) {
  const { busy, run } = useSave();
  const [removing, setRemoving] = useState<Department | null>(null);
  const people = new Map(data.people.map((p) => [p.id, p]));
  const members = (id: string) => data.people.filter((p) => p.department === id);
  if (!data.departments.length) return <EmptyState icon="folder" title={t('dir.no_departments')} body={data.can.manage ? t('dir.no_departments_hint_manage') : t('dir.no_departments_hint')} />;
  const tree = departmentOptions(data.departments);
  const byId = new Map(data.departments.map((d) => [d.id, d]));
  return (
    <>
      <ul className="dir-deps">
        {tree.map(({ id, depth }) => {
          const d = byId.get(id)!;
          const boss = d.manager ? people.get(d.manager) : null;
          const team = members(d.id);
          const inside = withInside(d.id, data.departments).size - 1;
          return (
            <li key={d.id} className={`dir-dep-card${depth ? ' is-inside' : ''}`} style={{ marginInlineStart: `calc(${Math.min(depth, 4)} * var(--space-5))` }}>
              <span className={`dir-dep-bar tone-${toneOf(d.color)}`} aria-hidden="true" />
              <div className="dir-dep-main">
                <div className="dir-dep-title">
                  <h3>{d.name}</h3>
                  {d.open_jobs ? <StatusBadge tone="info" icon="users" label={tn('dir.open_jobs', d.open_jobs)} /> : null}
                </div>
                {d.note ? <p className="muted small">{d.note}</p> : null}
                <div className="dir-dep-facts">
                  {boss ? (
                    <button type="button" className="dir-chip" onClick={() => onOpen(boss.id)}>
                      <Avatar name={boss.name} photo={boss.photo} size="sm" />
                      <span>
                        <span className="muted small">{t('dir.dep_manager')}</span> {boss.name}
                      </span>
                    </button>
                  ) : (
                    <span className="muted small">{t('dir.dep_no_manager')}</span>
                  )}
                  <span className="muted small">
                    {tn('dir.people_n', team.length)}
                    {inside ? ` · ${tn('dir.inside_n', inside)}` : ''}
                  </span>
                </div>
                {team.length ? (
                  <span className="dir-stack" aria-label={team.map((p) => p.name).join(', ')}>
                    {team.slice(0, 8).map((p) => (
                      <Avatar key={p.id} name={p.name} photo={p.photo} size="sm" />
                    ))}
                    {team.length > 8 ? <span className="dir-avatar sm dir-more">+{team.length - 8}</span> : null}
                  </span>
                ) : null}
              </div>
              <div className="dir-dep-actions">
                <Button size="sm" icon="users" onClick={() => onShow(d.id)}>
                  {t('dir.show_people')}
                </Button>
                {data.can.manage ? (
                  <>
                    <IconButton icon="edit" label={t('dir.edit_department_x', { name: d.name })} onClick={() => onEdit(d)} />
                    <IconButton icon="trash" label={t('dir.remove_department_x', { name: d.name })} onClick={() => setRemoving(d)} />
                  </>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {removing ? (
        <Confirm
          title={t('dir.remove_department_title', { name: removing.name })}
          body={t('dir.remove_department_body')}
          confirm={t('dir.remove')}
          danger
          busy={busy}
          onClose={() => setRemoving(null)}
          onConfirm={() =>
            void run(async () => {
              await action('hr.remove_department', { id: removing.id });
              setRemoving(null);
              await onChange();
            }, t('dir.department_removed'))
          }
        />
      ) : null}
    </>
  );
}

function DepartmentForm({ initial, all, onClose, onSaved }: { initial: Department | null; all: Department[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const { data: choices } = useAction<Choices>('hr.directory_choices');
  const { busy, run } = useSave();
  const [f, setF] = useState({ name: initial?.name ?? '', parent: initial?.parent ?? '', manager: initial?.manager ?? '', color: initial?.color ?? 0, note: initial?.note ?? '' });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const ok = f.name.trim().length >= 2;
  const blocked = initial ? withInside(initial.id, all) : undefined;
  return (
    <Dialog
      title={initial ? t('dir.edit_department') : t('dir.new_department')}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (!ok) return;
              void run(async () => {
                await action('hr.save_department', { id: initial?.id ?? null, name: f.name.trim(), parent: f.parent || null, manager: f.manager || null, color: f.color, note: f.note.trim() || null });
                await onSaved();
              }, t('dir.saved'));
            }}
          >
            {t('dir.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('dir.department_name')} error={tried && !ok ? t('dir.e_department_name') : null} required>
          <input className="input" value={f.name} maxLength={60} placeholder={t('dir.department_name_ph')} onChange={(e) => set('name', e.target.value)} data-autofocus />
        </Field>
        <div className="form-grid">
          <Field label={t('dir.inside_of')} hint={t('dir.optional')}>
            <select className="input" value={f.parent} onChange={(e) => set('parent', e.target.value)}>
              <option value="">{t('dir.top_level')}</option>
              {departmentOptions(all, blocked).map((d) => (
                <option key={d.id} value={d.id}>
                  {' '.repeat(d.depth)}
                  {d.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('dir.dep_manager')} hint={t('dir.optional')}>
            <select className="input" value={f.manager} onChange={(e) => set('manager', e.target.value)}>
              <option value="">—</option>
              {(choices?.people ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <fieldset className="dir-colors">
          <legend className="field-label">{t('dir.color')}</legend>
          {TONES.map((tone, i) => (
            <button
              type="button"
              key={tone}
              className={`dir-color tone-${tone}${f.color === i ? ' selected' : ''}`}
              aria-pressed={f.color === i}
              aria-label={t(`dir.color_${tone}` as MessageKey)}
              onClick={() => set('color', i)}
            >
              {f.color === i ? <Icon name="check" size={14} /> : null}
            </button>
          ))}
        </fieldset>
        <Field label={t('dir.department_note')} hint={t('dir.optional')}>
          <textarea className="input" rows={3} maxLength={500} value={f.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- a person's card ---------------------------------------------------------------------

/** Makes a picture square and small (256 px) before it is sent. */
async function squarePhoto(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error(t('dir.e_photo_read')));
      i.src = url;
    });
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    const c = document.createElement('canvas');
    c.width = c.height = 256;
    c.getContext('2d')!.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, 256, 256);
    return c.toDataURL('image/jpeg', 0.85).split(',')[1]!;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function PersonDialog({ id, today, onOpen, onClose, onChange }: { id: string; today: string; onOpen: (id: string) => void; onClose: () => void; onChange: () => Promise<void> }) {
  const { data: c, reload } = useAction<Card>('hr.person', { user: id });
  const { me } = useSession();
  const { busy, run } = useSave();
  const [editing, setEditing] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const changed = async () => {
    await reload();
    await onChange();
  };
  const setPhoto = (data: string | null) =>
    void run(
      async () => {
        await upload('hr.set_photo', { user: id, data });
        await changed();
      },
      data ? t('dir.photo_saved') : t('dir.photo_removed'),
    );
  const own = me?.userId === id;
  const since = c ? tenure(c.start_date, today) : null;
  return (
    <>
      <Dialog
        title={t('dir.person')}
        onClose={onClose}
        size="lg"
        footer={
          c && (c.can.edit_own || c.can.edit_work) ? (
            <span className="foot-actions">
              <Button variant="primary" icon="edit" onClick={() => setEditing(true)}>
                {own ? t('dir.edit_my_card') : t('dir.edit_card')}
              </Button>
            </span>
          ) : undefined
        }
      >
        {!c ? (
          <Skeleton h={260} />
        ) : (
          <div className="dir-person">
            <div className="dir-person-head">
              <div className="dir-photo">
                <Avatar name={c.name} photo={c.photo} size="xl" />
                {c.can.photo ? (
                  <span className="dir-photo-actions">
                    <IconButton icon="image" label={c.photo ? t('dir.change_photo') : t('dir.add_photo')} variant="secondary" disabled={busy} onClick={() => picker.current?.click()} />
                    {c.photo ? <IconButton icon="trash" label={t('dir.remove_photo')} variant="secondary" disabled={busy} onClick={() => setPhoto(null)} /> : null}
                    <input
                      ref={picker}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) void run(async () => setPhoto(await squarePhoto(file)));
                      }}
                    />
                  </span>
                ) : null}
              </div>
              <div className="dir-person-who">
                <h2>{c.name}</h2>
                <p>{c.job_title ?? t('dir.no_title')}</p>
                {c.department_path.length ? <p className="muted small">{c.department_path.join(' › ')}</p> : null}
                <div className="dir-person-badges">
                  {!c.active ? <StatusBadge tone="neutral" icon="archive" label={t('dir.left')} /> : null}
                  {c.off_until ? (
                    <StatusBadge tone="warn" icon="sun" label={t('dir.off_until', { day: dayText(c.off_until) })} />
                  ) : c.active ? (
                    <StatusBadge tone="ok" icon="checkCircle" label={t('dir.working_today')} />
                  ) : null}
                  {c.next_leave && !c.off_until ? <StatusBadge tone="info" icon="calendar" label={t('dir.off_from', { start: dayText(c.next_leave.start), end: dayText(c.next_leave.end) })} /> : null}
                  {c.roles.map((r) => (
                    <span key={r} className="dir-role">
                      {t(`role.${r}` as MessageKey)}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="dir-person-body">
              <section className="dir-sec">
                <h3>{t('dir.work')}</h3>
                <dl className="dir-facts">
                  <dt>{t('dir.work_phone')}</dt>
                  <dd>{c.work_phone ? <a href={`tel:${c.work_phone.replace(/[^+0-9]/g, '')}`}>{c.work_phone}</a> : <span className="muted">—</span>}</dd>
                  <dt>{t('dir.work_email')}</dt>
                  <dd className="dir-break">{c.work_email ? <a href={`mailto:${c.work_email}`}>{c.work_email}</a> : <span className="muted">—</span>}</dd>
                  <dt>{t('dir.place')}</dt>
                  <dd>{c.location?.name ?? <span className="muted">—</span>}</dd>
                  <dt>{t('dir.started')}</dt>
                  <dd>{c.start_date ? `${dayText(c.start_date)}${since ? ` · ${since}` : ''}` : <span className="muted">—</span>}</dd>
                  {c.birthday ? (
                    <>
                      <dt>{t('dir.birthday')}</dt>
                      <dd>
                        {c.birthday.length === 5 ? birthdayText(c.birthday) : dayText(c.birthday)}
                        {c.private ? <span className="muted small"> · {c.share_birthday ? t('dir.birthday_shared') : t('dir.birthday_private')}</span> : null}
                      </dd>
                    </>
                  ) : null}
                </dl>
                {c.about ? <p className="dir-about">{c.about}</p> : null}
              </section>

              <section className="dir-sec">
                <h3>{t('dir.place_in_team')}</h3>
                {c.chain.length ? (
                  <ol className="dir-chain" aria-label={t('dir.reports_to')}>
                    {c.chain
                      .slice()
                      .reverse()
                      .map((m) => (
                        <li key={m.id}>
                          <button type="button" className="dir-chip" onClick={() => onOpen(m.id)}>
                            <Avatar name={m.name} photo={m.photo} size="sm" />
                            <span>
                              {m.name}
                              {m.job_title ? <span className="muted small"> · {m.job_title}</span> : null}
                            </span>
                          </button>
                        </li>
                      ))}
                    <li aria-current="true">
                      <span className="dir-chip is-me">
                        <Avatar name={c.name} photo={c.photo} size="sm" />
                        <span>{c.name}</span>
                      </span>
                    </li>
                  </ol>
                ) : (
                  <p className="muted small">{t('dir.reports_to_nobody')}</p>
                )}
                {c.reports.length ? (
                  <>
                    <h4 className="dir-h4">{tn('dir.team_of', c.reports.length)}</h4>
                    <ul className="dir-chips">
                      {c.reports.map((r) => (
                        <li key={r.id}>
                          <button type="button" className="dir-chip" onClick={() => onOpen(r.id)}>
                            <Avatar name={r.name} photo={r.photo} size="sm" />
                            <span>
                              {r.name}
                              {r.job_title ? <span className="muted small"> · {r.job_title}</span> : null}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </section>

              {c.private ? (
                <section className="dir-sec dir-private">
                  <h3>
                    <Icon name="lock" size={16} />
                    {t('dir.private')}
                  </h3>
                  <p className="muted small">{own ? t('dir.private_hint_own') : t('dir.private_hint')}</p>
                  <dl className="dir-facts">
                    <dt>{t('dir.emergency')}</dt>
                    <dd>
                      {c.private.emergency_name || c.private.emergency_phone ? (
                        <>
                          {c.private.emergency_name ?? ''}
                          {c.private.emergency_phone ? (
                            <>
                              {c.private.emergency_name ? ' · ' : ''}
                              <a href={`tel:${c.private.emergency_phone.replace(/[^+0-9]/g, '')}`}>{c.private.emergency_phone}</a>
                            </>
                          ) : null}
                        </>
                      ) : (
                        <span className="muted">{t('dir.not_given')}</span>
                      )}
                    </dd>
                    <dt>{t('dir.private_phone')}</dt>
                    <dd>
                      {c.private.private_phone ? <a href={`tel:${c.private.private_phone.replace(/[^+0-9]/g, '')}`}>{c.private.private_phone}</a> : <span className="muted">{t('dir.not_given')}</span>}
                    </dd>
                  </dl>
                </section>
              ) : null}
            </div>
          </div>
        )}
      </Dialog>
      {editing && c ? (
        <ProfileForm
          card={c}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            await changed();
          }}
        />
      ) : null}
    </>
  );
}

function ProfileForm({ card, onClose, onSaved }: { card: Card; onClose: () => void; onSaved: () => Promise<void> }) {
  const { data: choices } = useAction<Choices>('hr.directory_choices');
  const { busy, run } = useSave();
  const [f, setF] = useState({
    job_title: card.job_title ?? '',
    department: card.department?.id ?? '',
    manager: card.manager?.id ?? '',
    location: card.location?.id ?? '',
    work_email: card.work_email ?? '',
    start_date: card.start_date ?? '',
    work_phone: card.work_phone ?? '',
    about: card.about ?? '',
    emergency_name: card.private?.emergency_name ?? '',
    emergency_phone: card.private?.emergency_phone ?? '',
    private_phone: card.private?.private_phone ?? '',
    birthday: card.birthday && card.birthday.length === 10 ? card.birthday : '',
    share_birthday: card.share_birthday,
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const phoneOk = (v: string) => /^[+0-9 ()\-./]*$/.test(v);
  const emailOk = !f.work_email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.work_email.trim());
  const ok = phoneOk(f.work_phone) && phoneOk(f.emergency_phone) && phoneOk(f.private_phone) && emailOk && (!f.birthday || f.birthday < todayIso());
  const work = card.can.edit_work;
  return (
    <Dialog
      title={t('dir.edit_card_of', { name: card.name })}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            onClick={() => {
              setTried(true);
              if (!ok) return;
              const trim = (v: string) => v.trim() || null;
              void run(async () => {
                await action('hr.save_profile', {
                  user: card.id,
                  ...(work
                    ? {
                        job_title: trim(f.job_title),
                        department: f.department || null,
                        manager: f.manager || null,
                        location: f.location || null,
                        work_email: trim(f.work_email),
                        start_date: f.start_date || null,
                      }
                    : {}),
                  work_phone: trim(f.work_phone),
                  about: trim(f.about),
                  emergency_name: trim(f.emergency_name),
                  emergency_phone: trim(f.emergency_phone),
                  private_phone: trim(f.private_phone),
                  birthday: f.birthday || null,
                  share_birthday: f.share_birthday,
                });
                await onSaved();
              }, t('dir.saved'));
            }}
          >
            {t('dir.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <fieldset className="dir-fieldset">
          <legend>{t('dir.work')}</legend>
          {work ? (
            <>
              <div className="form-grid">
                <Field label={t('dir.job_title')}>
                  <input className="input" value={f.job_title} maxLength={80} placeholder={t('dir.job_title_ph')} onChange={(e) => set('job_title', e.target.value)} data-autofocus />
                </Field>
                <Field label={t('dir.department')}>
                  <select className="input" value={f.department} onChange={(e) => set('department', e.target.value)}>
                    <option value="">—</option>
                    {departmentOptions(choices?.departments ?? []).map((d) => (
                      <option key={d.id} value={d.id}>
                        {' '.repeat(d.depth)}
                        {d.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="form-grid">
                <Field label={t('dir.reports_to')} hint={t('dir.reports_to_hint')}>
                  <select className="input" value={f.manager} onChange={(e) => set('manager', e.target.value)}>
                    <option value="">{t('dir.nobody')}</option>
                    {(choices?.people ?? [])
                      .filter((p) => p.id !== card.id)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                  </select>
                </Field>
                <Field label={t('dir.place')}>
                  <select className="input" value={f.location} onChange={(e) => set('location', e.target.value)}>
                    <option value="">—</option>
                    {(choices?.locations ?? []).map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="form-grid">
                <Field label={t('dir.work_email')} error={tried && !emailOk ? t('dir.e_email') : null}>
                  <input className="input" type="email" value={f.work_email} maxLength={160} onChange={(e) => set('work_email', e.target.value)} />
                </Field>
                <Field label={t('dir.started')}>
                  <input className="input" type="date" value={f.start_date} onChange={(e) => set('start_date', e.target.value)} />
                </Field>
              </div>
            </>
          ) : (
            <p className="muted small">{t('dir.work_by_manager')}</p>
          )}
          <Field label={t('dir.work_phone')} error={tried && !phoneOk(f.work_phone) ? t('dir.e_phone') : null}>
            <input className="input" type="tel" value={f.work_phone} maxLength={40} onChange={(e) => set('work_phone', e.target.value)} {...(work ? {} : { 'data-autofocus': true })} />
          </Field>
          <Field label={t('dir.about')} hint={t('dir.about_hint')}>
            <textarea className="input" rows={3} maxLength={1000} value={f.about} onChange={(e) => set('about', e.target.value)} />
          </Field>
        </fieldset>
        <fieldset className="dir-fieldset">
          <legend>
            <Icon name="lock" size={14} /> {t('dir.private')}
          </legend>
          <p className="muted small">{t('dir.private_form_hint')}</p>
          <div className="form-grid">
            <Field label={t('dir.emergency_name')} hint={t('dir.emergency_name_hint')}>
              <input className="input" value={f.emergency_name} maxLength={120} onChange={(e) => set('emergency_name', e.target.value)} />
            </Field>
            <Field label={t('dir.emergency_phone')} error={tried && !phoneOk(f.emergency_phone) ? t('dir.e_phone') : null}>
              <input className="input" type="tel" value={f.emergency_phone} maxLength={40} onChange={(e) => set('emergency_phone', e.target.value)} />
            </Field>
          </div>
          <div className="form-grid">
            <Field label={t('dir.private_phone')} error={tried && !phoneOk(f.private_phone) ? t('dir.e_phone') : null}>
              <input className="input" type="tel" value={f.private_phone} maxLength={40} onChange={(e) => set('private_phone', e.target.value)} />
            </Field>
            <Field label={t('dir.birthday')} error={tried && f.birthday && f.birthday >= todayIso() ? t('dir.e_birthday') : null}>
              <input className="input" type="date" value={f.birthday} max={todayIso()} onChange={(e) => set('birthday', e.target.value)} />
            </Field>
          </div>
          <div className="setting-row">
            <div>
              <strong>{t('dir.share_birthday')}</strong>
              <p className="muted">{t('dir.share_birthday_hint')}</p>
            </div>
            <Switch checked={f.share_birthday} disabled={!f.birthday} onChange={(v) => set('share_birthday', v)} label={t('dir.share_birthday')} />
          </div>
        </fieldset>
      </div>
    </Dialog>
  );
}
